<script setup>
import TheCustomerMetaNavigation from '@/components/basic/atoms/TheCustomerMetaNavigation.vue'
import UspBar from '@/components/basic/atoms/UspBar.vue'
import TheHeader from '@/components/basic/organisms/TheHeader.vue'
import TheFilter from '@/components/category/organisms/TheFilter.vue'
import { TYPES as AdminToolTypes } from '@/constants/AdminTool'
import { ASSET_DOWNLOAD_URL } from '@/constants/CollectionPageConstants'
import {
  containsPrivateInfo,
  NO_INDEX_FOLLOW
} from '@/services/helpers/MetaHelper.js'
import { useCollectionPageStore } from '@/stores/collectionPage'
import { useCustomerStore } from '@/stores/customer'
import { useSearchConfig } from '@/stores/searchConfig'
import { useHead } from '@unhead/vue'
import DOMPurify from 'dompurify'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, nextTick, watch } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { useRecommendationStore } from '../stores/recommendations.js'

const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)
const AppBanner = defineAsyncComponent(
  () => import('@/components/basic/molecules/AppBanner.vue')
)
const TheFooter = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheFooter.vue')
)
const MultiTools = defineAsyncComponent(
  () => import('@/components/basic/molecules/MultiTools.vue')
)
const Tool = defineAsyncComponent(
  () => import('@/components/basic/atoms/Tool.vue')
)

const collectionPageStore = useCollectionPageStore()
const searchConfig = useSearchConfig()
const route = useRoute()
const { customerIsAdmin } = storeToRefs(useCustomerStore())

const page = computed(() => collectionPageStore.page)
// Editorial HTML comes pre-rendered from the platform API (server-side TipTap →
// HTML). We sanitize defensively before v-html so a malformed or compromised
// payload cannot inject script tags or event handlers into the page.
const editorialHtml = computed(() =>
  page.value?.editorial_text
    ? DOMPurify.sanitize(page.value.editorial_text)
    : ''
)
// SEO copy below the product grid, the collection-page successor of the legacy
// category "After HTML". Same pipeline as the editorial text above the grid:
// server-rendered by the platform API, sanitized here before v-html.
const outroHtml = computed(() =>
  page.value?.outro_text ? DOMPurify.sanitize(page.value.outro_text) : ''
)
const category = computed(() => searchConfig.category)
const categoryError = computed(() => searchConfig.config.error)
const title = computed(() => page.value?.meta_title || page.value?.title || '')
const showPageTitle = computed(
  () => page.value?.show_title !== false && !!page.value?.title
)

const assetUrl = (id) => `${ASSET_DOWNLOAD_URL}${id}/download`
const hasTeaser = computed(
  () =>
    page.value?.desktop_asset_id ||
    page.value?.tablet_asset_id ||
    page.value?.mobile_asset_id
)
const teaserFallbackUrl = computed(() => {
  const p = page.value
  return assetUrl(
    p?.desktop_asset_id || p?.tablet_asset_id || p?.mobile_asset_id
  )
})

const recommendations = useRecommendationStore()
recommendations.fetch()

searchConfig.fetchCollectionPageProducts(route.params.id)

let lastId = route.params.id

watch(
  () => [route.params.id, JSON.stringify(route.query)],
  async ([id]) => {
    // Reload whenever the URL changes — including browser back/forward, where
    // the query reverts but nothing else triggers a fetch (VIP-11940). A full
    // reset is only needed when the collection itself changes; query-only
    // changes (filters/sort/pagination) keep the page and just refetch. The
    // matching imperative fetch from applyFilter is de-duped in the store.
    if (id !== lastId) {
      lastId = id
      searchConfig.resetConfig()
    }
    await nextTick()
    await searchConfig.fetchCollectionPageProducts(id)
  }
)

onBeforeRouteLeave(() => {
  collectionPageStore.$reset()
  searchConfig.resetConfig()
})

useHead({
  title,
  meta: [
    {
      name: 'robots',
      content: computed(() =>
        containsPrivateInfo() ? NO_INDEX_FOLLOW : page.value?.robots
      )
    },
    {
      name: 'description',
      content: computed(() => page.value?.meta_description)
    }
  ],
  link: [
    {
      rel: 'canonical',
      href: computed(() => route.path)
    }
  ]
})
</script>

<template>
  <div
    class="position-relative"
    id="content"
    data-elbglobals="page_type:collection"
    data-elbcontext="shopping:discover"
  >
    <AppBanner />
    <UspBar />
    <TheCustomerMetaNavigation />
    <TheHeader class="position-relative" />
    <main id="page-template">
      <div v-if="showPageTitle" class="v-container my-5">
        <div class="v-col-12 py-5">
          <h1
            class="font-garbata text-center font-size-xxxxl font-size-md-xxxxxl font-weight-bold c-winered"
          >
            {{ page.title }}
          </h1>
        </div>
      </div>
      <div v-if="hasTeaser || page.editorial_text" class="v-container mb-5">
        <div class="collection-page-card rounded">
          <picture v-if="hasTeaser">
            <source
              v-if="page.desktop_asset_id"
              :srcset="assetUrl(page.desktop_asset_id)"
              media="(min-width: 1280px)"
            />
            <source
              v-if="page.tablet_asset_id"
              :srcset="assetUrl(page.tablet_asset_id)"
              media="(min-width: 768px)"
            />
            <source
              v-if="page.mobile_asset_id"
              :srcset="assetUrl(page.mobile_asset_id)"
              media="(max-width: 767px)"
            />
            <img
              :src="teaserFallbackUrl"
              :alt="page.title"
              class="collection-page-card__image w-100"
            />
          </picture>
          <div
            v-if="editorialHtml"
            class="collection-page-card__editorial"
            v-html="editorialHtml"
          ></div>
        </div>
      </div>
      <p v-if="categoryError" class="text-center py-10">
        {{ $t('cms_loading_error') }}
      </p>
      <template v-else>
        <TheFilter v-if="category.code" />
        <div
          v-if="outroHtml"
          class="v-container collection-page-outro styled-text"
          v-html="outroHtml"
        ></div>
      </template>
    </main>
    <LazyComponent id="footer" :delay="2000">
      <TheFooter />
    </LazyComponent>
    <MultiTools id="fixed-tools" v-if="customerIsAdmin">
      <Tool :type="AdminToolTypes.PRODUCT_FILTER_OVERRIDES" />
      <Tool
        :type="AdminToolTypes.EDIT_COLLECTION_PAGE"
        :id="page.collection_page_site_id"
      />
    </MultiTools>
  </div>
</template>

<style scoped>
.collection-page-card {
  overflow: hidden;
  background-color: #fff;
}

.collection-page-card__image {
  display: block;
}

.collection-page-card__editorial {
  padding: 24px;
}

.collection-page-card__editorial :deep(:first-child) {
  margin-top: 0;
}

.collection-page-card__editorial :deep(:last-child) {
  margin-bottom: 0;
}

/*
 * Typography for the server-rendered outro text. Central on purpose: the legacy
 * category pages carried a per-page CSS field for this, which the collection
 * pages deliberately do not offer.
 */
.collection-page-outro {
  padding-top: 32px;
  padding-bottom: 32px;
}

.collection-page-outro :deep(h2),
.collection-page-outro :deep(h3),
.collection-page-outro :deep(h4) {
  font-family: var(--garbata-font-family);
  line-height: 130%;
  letter-spacing: normal;
  margin: 24px 0 8px;
}

.collection-page-outro :deep(h2) {
  font-size: var(--xxl-font-size);
}

.collection-page-outro :deep(h3) {
  font-size: var(--xl-font-size);
}

.collection-page-outro :deep(h4) {
  font-size: var(--l-font-size);
}

/*
 * Links are styled here rather than with the `link-winered` utility: that
 * selector also matches the element it sits on, so the whole block inherited
 * the link colour, a pointer cursor and the full-width hover underline.
 */
.collection-page-outro :deep(a) {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}

.collection-page-outro :deep(a:hover) {
  text-decoration: none;
}

.collection-page-outro :deep(ul),
.collection-page-outro :deep(ol) {
  margin: 0 0 16px;
  padding-left: 20px;
}

.collection-page-outro :deep(:first-child) {
  margin-top: 0;
}

.collection-page-outro :deep(:last-child) {
  margin-bottom: 0;
}
</style>
